import type { EmployeeListResponse } from 'src/api/generated';
import { PiEye, PiPencilSimple } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { IconBadge } from 'src/modules/shared/components/atoms/icon-badge';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';

interface EmployeeCardProps {
  /** AI Agent displayed by the card. */
  employee: EmployeeListResponse['items'][number];
  /** Opens the AI Agent editor. */
  onEdit?: () => void;
  /** Opens the AI Agent execution history. */
  onViewExecutions: () => void;
}

/** Responsive AI Agent card built from shared design-system primitives. */
const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  onEdit,
  onViewExecutions,
}) => {
  const { t } = useI18n();
  const name =
    employee.personalityName ?? t('automations.aiAgents.index.unnamed');
  const identifier = employee.name;
  return (
    <Card
      appearance="outline"
      shadow="none"
      className="border-neutral-200 bg-white"
    >
      <CardBody className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <IconBadge
            icon={<AiRobotOutlineIcon className="h-7 w-7 text-blue-500" />}
            size="lg"
            variant="primary"
            appearance="soft"
            className="shrink-0 self-center"
          />
          <div className="min-w-0 flex-1">
            <Text
              as="h2"
              size="h5"
              weight="medium"
              className="truncate text-primary-950"
            >
              {name}
            </Text>
            {identifier ? (
              <Text size="small" color="secondary" className="mt-0.5">
                {identifier}
              </Text>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row">
            {onEdit ? (
              <Button
                size="sm"
                variant="secondary"
                appearance="outline"
                onClick={onEdit}
                className="w-full gap-2 sm:w-auto"
              >
                <PiPencilSimple className="h-4 w-4" />
                {t('automations.aiAgents.index.edit')}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="primary"
              appearance="soft"
              onClick={onViewExecutions}
              className="w-full gap-2 sm:w-auto"
            >
              <PiEye className="h-4 w-4" />
              {t('automations.aiAgents.index.executions')}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default EmployeeCard;
